import { createContext, PropsWithChildren, useCallback, useReducer } from 'react';

import useToastPresence from './useToastPresence';
import { toastReducer, toastActions } from './utils';
import ToastList from '../../modules/ToastList/ToastList';
import { ToastId, ToastOptions, NotificationToastOptions, AnyToastItem } from '../../modules/ToastList/types';

export type ToastContextType = {
  toastState: AnyToastItem[];
  pushToast: (options: ToastOptions) => void;
  pushNotificationToast: (options: NotificationToastOptions) => void;
  removeToast: (id: ToastId) => void;
  editToast: (id: ToastId, options: ToastOptions) => void;
  editNotificationToast: (id: ToastId, options: NotificationToastOptions) => void;
  isToastPresent: (toastId: string) => boolean;
  clearAllToasts: () => void;
};

export const ToastContext = createContext<ToastContextType | null>(null);

export const ToastProvider = ({ children }: PropsWithChildren) => {
  const [toastState, dispatch] = useReducer(toastReducer, []);
  const pushToast = useCallback((options: ToastOptions) => dispatch(toastActions.pushToast(options)), []);
  const pushNotificationToast = useCallback(
    (options: NotificationToastOptions) => dispatch(toastActions.pushNotificationToast(options)),
    []
  );
  const removeToast = useCallback((id: ToastId) => dispatch(toastActions.removeToast(id)), []);
  const editToast = useCallback(
    (id: ToastId, options: ToastOptions) => dispatch(toastActions.editToast(id, options)),
    []
  );
  const editNotificationToast = useCallback(
    (id: ToastId, options: NotificationToastOptions) => dispatch(toastActions.editNotificationToast(id, options)),
    []
  );
  const clearAllToasts = useCallback(() => dispatch(toastActions.clearAllToasts()), []);
  const isToastPresent = useToastPresence(toastState);

  return (
    <ToastContext.Provider
      value={{
        toastState,
        pushToast,
        pushNotificationToast,
        removeToast,
        editToast,
        editNotificationToast,
        isToastPresent,
        clearAllToasts,
      }}
    >
      {children}
      {!!toastState.length && <ToastList toastState={toastState} />}
    </ToastContext.Provider>
  );
};
